import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  LinearProgress,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { useParams, useNavigate } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import AddIcon from "@mui/icons-material/Add";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import FolderIcon from "@mui/icons-material/Folder";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";

export default function MobileFolderProducts() {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    product_name: "",
    sku: "",
    price: "",
    quantity: "",
    web_link: "",
    tags: "",
    notes: "",
    image: null,
    imagePreview: "",
  });
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  // New move dialog states
  const [moveTargets, setMoveTargets] = useState([]);
  const [selectedMoveTargetId, setSelectedMoveTargetId] = useState(null);
  const [searchText, setSearchText] = useState("");
  // Search/filter for products
  const [searchTerm, setSearchTerm] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [showExportProgress, setShowExportProgress] = useState(false);
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({
      ...prev,
      image: file,
      imagePreview: previewUrl,
    }));
  };

  const handleAddProduct = async () => {
    try {
      const form = new FormData();
      form.append("product_name", formData.product_name);
      form.append("sku", formData.sku);
      form.append("price", formData.price);
      form.append("quantity", formData.quantity);
      form.append("web_link", formData.web_link);
      form.append("tags", formData.tags);
      form.append("notes", formData.notes);
      form.append("folder_id", folderId);

      if (formData.image instanceof File) {
        form.append("image", formData.image);
      }

      await axios.post(
        "https://server-9p4hx.kinsta.app/api/stocmanager/products/upload-image",
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setDialogOpen(false);
      setSnackbarOpen(true);
      setFormData({
        product_name: "",
        sku: "",
        price: "",
        quantity: "",
        web_link: "",
        tags: "",
        notes: "",
        image: null,
        imagePreview: "",
      });
      fetchProducts();
    } catch (err) {
      console.error("Eroare la salvare produs în S3:", err);
      alert("Eroare la salvare produs.");
    }
  };

  useEffect(() => {
    fetchFolders();
    fetchProducts();
  }, [folderId]);

  const fetchFolders = async () => {
    const res = await axios.get(
      "https://server-9p4hx.kinsta.app/api/stocmanager/folders",
    );
    setFolders(res.data);
  };

  // Fetch folders for move dialog (separate, for up-to-date list)
  const fetchMoveTargets = async () => {
    try {
      const res = await axios.get(
        "https://server-9p4hx.kinsta.app/api/stocmanager/folders",
      );
      setMoveTargets(res.data);
    } catch (err) {
      setMoveTargets([]);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${folderId}/products`,
      );
      setProducts(res.data);
      console.log("📦 Produse:", res.data);
    } catch (err) {
      console.error("Eroare la fetch produse:", err);
    } finally {
      setLoading(false);
    }
  };

  const totalValue = products.reduce(
    (acc, prod) => acc + parseFloat(prod.price || 0) * (prod.quantity || 1),
    0,
  );
  const folderName =
    folders.find((f) => f.id === parseInt(folderId))?.name || "";

  const pollExportProgress = (progressId) => {
    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        try {
          const res = await axios.get(
            `https://server-9p4hx.kinsta.app/api/stocmanager/folders/export-progress/${progressId}`,
          );
          const value = Number(res.data?.progress ?? 0);
          setExportProgress(value);
          if (value >= 100) {
            break;
          }
        } catch (error) {
          console.error("Eroare la interogarea progresului exportului:", error);
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 600));
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  };

  const handleExportPdf = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    setExportProgress(0);
    setShowExportProgress(true);
    let stopPolling = null;
    try {
      const progressId = `${folderId}-${Date.now()}`;
      stopPolling = pollExportProgress(progressId);
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${folderId}/export-stock-pdf?progressId=${progressId}`,
        {
          responseType: "blob",
        },
      );
      if (stopPolling) stopPolling();
      setExportProgress(100);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = (folderName || "folder").replace(/\s+/g, "_");
      link.href = url;
      link.setAttribute(
        "download",
        `Stoc_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (stopPolling) stopPolling();
      console.error("Eroare la exportul PDF:", error);
      const message =
        error?.response?.data?.error ||
        "Nu am reușit să exportăm PDF-ul. Încearcă din nou.";
      alert(message);
    } finally {
      setExportingPdf(false);
      setTimeout(() => setShowExportProgress(false), 1500);
    }
  };

  return (
    <Container
      disableGutters
      sx={{
        pt: 2,
        px: "calc(3 * 8px - 30px)",
        color: "#fff",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {folderName}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            onClick={(event) => setActionsAnchorEl(event.currentTarget)}
            sx={{ color: "#fff" }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => navigate(-1)}
            size="small"
            sx={{ color: "#fff" }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={() => setActionsAnchorEl(null)}
      >
        <MenuItem
          onClick={async () => {
            setActionsAnchorEl(null);
            await handleExportPdf();
          }}
          disabled={exportingPdf}
        >
          {exportingPdf ? "Export în curs..." : "Export Stoc PDF"}
        </MenuItem>
      </Menu>
      {showExportProgress && (
        <Box
          sx={{
            position: "fixed",
            bottom: 20,
            left: 20,
            right: 20,
            bgcolor: "rgba(0,0,0,0.75)",
            color: "#fff",
            borderRadius: 2,
            p: 2,
            zIndex: 2000,
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            Generare PDF: {Math.min(exportProgress, 100)}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(exportProgress, 100)}
            sx={{
              height: 8,
              borderRadius: 1,
              bgcolor: "rgba(255,255,255,0.2)",
            }}
          />
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 2, bgcolor: "rgba(0,0,0,0.35)", color: "#fff" }}>
        <Box display="flex" justifyContent="space-between">
          <Box>
            <Typography fontSize={12}>Items</Typography>
            <Typography fontWeight="bold" fontSize={13}>
              {products.length}
            </Typography>
          </Box>
          <Box>
            <Typography fontSize={12}>Produse</Typography>
            <Typography fontWeight="bold" fontSize={13}>
              {products.reduce((acc, p) => acc + Number(p.quantity || 0), 0)}
            </Typography>
          </Box>
          <Box>
            <Typography fontSize={12}>Ciorne</Typography>
            <Typography fontWeight="bold" fontSize={13}>
              {products.filter((p) => !p.sku || p.sku.trim() === "").length}
            </Typography>
          </Box>
          <Box>
            <Typography fontSize={12}>Valoare</Typography>
            <Typography fontWeight="bold" fontSize={13}>
              {Math.round(totalValue).toLocaleString()} RON
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Search bar for filtering products */}
      <TextField
        placeholder="Caută după denumire sau SKU"
        variant="outlined"
        margin="dense"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#fff" }} />
            </InputAdornment>
          ),
          sx: {
            borderRadius: 3,
            backgroundColor: "rgba(0,0,0,0.35)",
            color: "#fff",
            "& input": { color: "#fff" },
          },
        }}
        sx={{
          mb: 2,
          ml: 0.5,
          width: "calc(100% - 15px)",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.6)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#fff",
          },
        }}
      />

      {loading ? (
        <CircularProgress />
      ) : products.length === 0 ? (
        <Typography>Nu există produse în acest folder.</Typography>
      ) : (
        <Box>
          {products
            .filter(
              (p) =>
                p.product_name
                  ?.toLowerCase()
                  .includes(searchTerm.toLowerCase()) ||
                p.sku?.toLowerCase().includes(searchTerm.toLowerCase()),
            )
            .map((prod) => (
              <Box
                key={prod.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                borderBottom="1px solid #eee"
                py={1.5}
                onClick={() => navigate(`/mobile-product/${prod.id}`)}
                sx={{
                  cursor: "pointer",
                  ...(prod.quantity === 0 &&
                  (!prod.sku || prod.sku.trim() === "")
                    ? {
                        background:
                          "linear-gradient(90deg, rgba(255,0,0,0.15), rgba(0,123,255,0.15))",
                      }
                    : prod.quantity === 0
                      ? {
                          boxShadow: "0 0 12px 2px rgba(255, 0, 0, 0.5)",
                        }
                      : {}),
                }}
              >
                <Box display="flex" alignItems="center">
                  {(prod.image_url || prod.image_base64) && (
                    <Box
                      component="img"
                      src={prod.image_url || prod.image_base64}
                      loading="lazy"
                      alt={prod.product_name}
                      sx={{
                        width: 90,
                        height: 90,
                        objectFit: "cover",
                        objectPosition: "center",
                        borderRadius: 1,
                        mr: 1,
                        backgroundColor: "#f5f5f5",
                        aspectRatio: "1 / 1",
                      }}
                    />
                  )}
                  <Box>
                    <Typography fontWeight="bold" fontSize={13}>
                      {prod.product_name}
                    </Typography>
                    {prod.quantity === 0 && (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        mt={0.5}
                      >
                        <NotificationsNoneIcon fontSize="small" color="error" />
                        <Typography fontSize={11} color="error">
                          Stoc epuizat
                        </Typography>
                      </Box>
                    )}
                    <Box display="flex" gap={2}>
                      <Typography fontSize={11}>SKU: {prod.sku}</Typography>
                      <Typography fontSize={11}>
                        Preț: {Math.round(prod.price).toLocaleString()} RON
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedProduct(prod);
                    setAnchorEl(e.currentTarget);
                  }}
                  sx={{ color: "#fff" }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
            ))}
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedProduct) {
              setFormData({
                product_name: (selectedProduct.product_name || "") + " (copie)",
                sku: selectedProduct.sku || "",
                price: selectedProduct.price || "",
                quantity: selectedProduct.quantity || "",
                web_link: selectedProduct.web_link || "",
                tags: selectedProduct.tags || "",
                notes: selectedProduct.notes || "",
                image: selectedProduct.image_base64 || null,
              });
              setDialogOpen(true);
            }
          }}
        >
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Clonează
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            fetchMoveTargets();
            setMoveDialogOpen(true);
          }}
        >
          <DriveFileMoveIcon fontSize="small" sx={{ mr: 1 }} /> Mută
        </MenuItem>

        <MenuItem
          onClick={async () => {
            setAnchorEl(null);
            await axios.delete(
              `https://server-9p4hx.kinsta.app/api/stocmanager/products/${selectedProduct?.id}`,
            );
            fetchProducts();
            console.log("📦 Produs șters cu ID:", selectedProduct?.id);
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Șterge
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Produsul a fost adăugat!
        </Alert>
      </Snackbar>

      {!dialogOpen && (
        <IconButton
          onClick={() => setDialogOpen(true)}
          sx={{
            position: "fixed",
            bottom: 80,
            right: 10,
            width: 56,
            height: 56,
            borderRadius: "50%",
            bgcolor: "primary.main",
            color: "#fff",
            zIndex: 9999,
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          <AddIcon />
        </IconButton>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullScreen>
        <DialogTitle>Adaugă Produs</DialogTitle>
        <DialogContent>
          <Box
            component="label"
            sx={{
              width: "100%",
              height: 180,
              backgroundColor: "#f9f9f9",
              border: "2px dashed #ccc",
              borderRadius: 2,
              mb: 2,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            {formData.imagePreview ? (
              <img
                src={formData.imagePreview}
                alt="preview"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center">
                <ImageSearchIcon fontSize="large" color="action" />
                <Typography fontSize={12} sx={{ color: "#fff" }}>
                  Încarcă imagine
                </Typography>
              </Box>
            )}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
            />
          </Box>

          <Typography fontSize={12} fontWeight="bold" mb={1}>
            Gestiune: {folderName}
          </Typography>

          <TextField
            fullWidth
            label="Product Name"
            margin="dense"
            value={formData.product_name}
            onChange={(e) =>
              setFormData({ ...formData, product_name: e.target.value })
            }
          />

          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              label="Quantity"
              margin="dense"
              type="number"
              value={formData.quantity}
              onChange={(e) =>
                setFormData({ ...formData, quantity: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Preț (€)"
              margin="dense"
              type="number"
              value={formData.price}
              onChange={(e) =>
                setFormData({ ...formData, price: e.target.value })
              }
            />
          </Box>

          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              label="SKU"
              margin="dense"
              value={formData.sku}
              onChange={(e) =>
                setFormData({ ...formData, sku: e.target.value })
              }
              sx={{ backgroundColor: "#f8e5f8" }}
            />
            <TextField
              fullWidth
              label="Tags"
              margin="dense"
              value={formData.tags}
              onChange={(e) =>
                setFormData({ ...formData, tags: e.target.value })
              }
            />
          </Box>

          <TextField
            fullWidth
            label="Notes"
            margin="dense"
            multiline
            rows={3}
            value={formData.notes}
            onChange={(e) =>
              setFormData({ ...formData, notes: e.target.value })
            }
          />

          <TextField
            fullWidth
            label="Web Link"
            margin="dense"
            value={formData.web_link}
            onChange={(e) =>
              setFormData({ ...formData, web_link: e.target.value })
            }
            sx={{ backgroundColor: "#f8e5f8" }}
          />
        </DialogContent>
        <DialogActions>
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            px={2}
            pb={2}
          >
            <Button
              onClick={() => setDialogOpen(false)}
              color="secondary"
              variant="outlined"
              fullWidth
              startIcon={<CloseIcon />}
              sx={{ mr: 1 }}
            >
              Anulează
            </Button>
            <Button
              onClick={handleAddProduct}
              variant="contained"
              fullWidth
              startIcon={<SaveIcon />}
              sx={{ ml: 1 }}
            >
              Salvează
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        fullScreen
      >
        <DialogTitle>Mută produs</DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Box
              component="img"
              src={selectedProduct?.image_base64}
              alt={selectedProduct?.product_name}
              sx={{
                width: 60,
                height: 60,
                objectFit: "cover",
                borderRadius: 1,
              }}
            />
            <Box>
              <Typography fontWeight="bold" noWrap maxWidth="200px">
                {selectedProduct?.product_name}
              </Typography>
              <Typography variant="caption" sx={{ color: "#fff" }}>
                {folders.find((f) => f.id === selectedProduct?.folder_id)
                  ?.name || "Necunoscut"}
              </Typography>
            </Box>
          </Box>

          <TextField
            label="Caută gestiune"
            fullWidth
            margin="normal"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Box mt={1} maxHeight="50vh" overflow="auto">
            {moveTargets
              .filter((layer1) => {
                const subfolders = moveTargets.filter(
                  (sub) =>
                    sub.parent_id === layer1.id &&
                    sub.name.toLowerCase().includes(searchText.toLowerCase()) &&
                    sub.id !== selectedProduct?.folder_id,
                );

                const isLayer1Match =
                  layer1.name
                    .toLowerCase()
                    .includes(searchText.toLowerCase()) &&
                  layer1.id !== selectedProduct?.folder_id;

                return isLayer1Match || subfolders.length > 0;
              })
              .map((layer1) => {
                const subfolders = moveTargets.filter(
                  (sub) =>
                    sub.parent_id === layer1.id &&
                    sub.name.toLowerCase().includes(searchText.toLowerCase()) &&
                    sub.id !== selectedProduct?.folder_id,
                );

                const isLayer1Match =
                  layer1.name
                    .toLowerCase()
                    .includes(searchText.toLowerCase()) &&
                  layer1.id !== selectedProduct?.folder_id;

                return (
                  <Box key={layer1.id} sx={{ mb: 2 }}>
                    {isLayer1Match && (
                      <Paper
                        onClick={() => setSelectedMoveTargetId(layer1.id)}
                        sx={{
                          p: 1.5,
                          mb: 0.5,
                          cursor: "pointer",
                          bgcolor:
                            selectedMoveTargetId === layer1.id
                              ? "#e3f2fd"
                              : "#fff",
                          borderLeft: "4px solid #1976d2",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              minWidth: 60,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 1,
                            }}
                          >
                            <StorefrontIcon
                              color="primary"
                              sx={{ fontSize: 38 }}
                            />
                          </Box>
                          <Box>
                            <Typography fontWeight="bold">
                              {layer1.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#fff" }}
                            >
                              Gestiune principală (Layer 1)
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    )}

                    {subfolders.map((sub) => (
                      <Paper
                        key={sub.id}
                        onClick={() => setSelectedMoveTargetId(sub.id)}
                        sx={{
                          ml: 4,
                          p: 1.5,
                          mb: 0.5,
                          cursor: "pointer",
                          bgcolor:
                            selectedMoveTargetId === sub.id
                              ? "#f3e5f5"
                              : "#f9f9f9",
                          borderLeft: "4px solid #9c27b0",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              minWidth: 60,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 1,
                            }}
                          >
                            <StorefrontIcon
                              color="secondary"
                              sx={{ fontSize: 38 }}
                            />
                          </Box>
                          <Box>
                            <Typography fontWeight="bold">
                              {sub.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#fff" }}
                            >
                              Subfolder (Layer 2)
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                );
              })}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            mt: 0,
            mb: 0.5,
            px: 2,
          }}
        >
          <Button
            onClick={() => setMoveDialogOpen(false)}
            color="secondary"
            variant="outlined"
            fullWidth
            startIcon={<CloseIcon />}
            sx={{ flex: 1 }}
          >
            Anulează
          </Button>
          <Button
            onClick={async () => {
              await axios.put(
                `https://server-9p4hx.kinsta.app/api/stocmanager/products/${selectedProduct.id}`,
                {
                  folder_id: selectedMoveTargetId,
                },
              );
              setMoveDialogOpen(false);
              setSelectedMoveTargetId(null);
              fetchProducts();
            }}
            variant="contained"
            fullWidth
            startIcon={<SwapHorizIcon />}
            sx={{ flex: 1 }}
            disabled={!selectedMoveTargetId}
          >
            Confirmă
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
